/**
 * Three Card Poker — server-authoritative rules engine (2–6 players against the dealer, session game with chips).
 *
 * Rules implemented:
 *  - One 52-card deck, reshuffled before every round. Each player antes (and may add a Pair Plus side bet), then everyone and
 *    the dealer get three cards. The dealer's hand stays hidden until every player has decided.
 *  - Hand order, high to low: straight flush, three of a kind, straight, flush, pair, high card. A-2-3 is the lowest straight and
 *    A-K-Q the highest.
 *  - Each player, in seat order, either plays (a bet equal to the ante) or folds (the ante is lost).
 *  - The dealer qualifies with queen-high or better. When the dealer does not qualify the ante pays 1:1 and the play bet is returned.
 *    Otherwise the better hand wins the ante and the play bet at 1:1, the worse loses both and a tie returns both.
 *  - Ante bonus, paid to a player who plays whatever the dealer holds: straight 1:1, three of a kind 4:1, straight flush 5:1.
 *  - Pair Plus pays on the player's own hand alone (even after folding): pair 1:1, flush 4:1, straight 6:1, three of a kind 30:1,
 *    straight flush 40:1.
 */
import { buildDeck, parseCard, type CardId } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  standingsFromScores,
  type EngineEvent,
  type GameEngine,
  type SeatSetup,
} from '../../core/types.js';

export type ThreeCardPokerCategory = 'HIGH_CARD' | 'PAIR' | 'FLUSH' | 'STRAIGHT' | 'THREE_OF_A_KIND' | 'STRAIGHT_FLUSH';

const CATEGORY_RANK: Record<ThreeCardPokerCategory, number> = {
  HIGH_CARD: 1,
  PAIR: 2,
  FLUSH: 3,
  STRAIGHT: 4,
  THREE_OF_A_KIND: 5,
  STRAIGHT_FLUSH: 6,
};

/** Paid on the ante to a player who plays, whatever the dealer holds. */
export const THREE_CARD_POKER_ANTE_BONUS: Partial<Record<ThreeCardPokerCategory, number>> = { STRAIGHT: 1, THREE_OF_A_KIND: 4, STRAIGHT_FLUSH: 5 };
/** Pair Plus pay table (to one). */
export const THREE_CARD_POKER_PAIR_PLUS: Partial<Record<ThreeCardPokerCategory, number>> = { PAIR: 1, FLUSH: 4, STRAIGHT: 6, THREE_OF_A_KIND: 30, STRAIGHT_FLUSH: 40 };

export interface ThreeCardPokerHandValue {
  category: ThreeCardPokerCategory;
  /** Totally ordered strength; higher wins. */
  value: number;
}

const pack = (cat: number, a: number, b = 0, c = 0) => cat * 15 ** 3 + a * 15 ** 2 + b * 15 + c;

export function threeCardPokerEvaluate(cards: readonly CardId[]): ThreeCardPokerHandValue {
  if (cards.length !== 3) throw new Error('Three Card Poker hands have exactly 3 cards');
  const parsed = cards.map(parseCard);
  const [r0, r1, r2] = parsed.map((c) => c.rank).sort((a, b) => b - a) as [number, number, number];
  const flush = parsed.every((c) => c.suit === parsed[0]!.suit);
  if (r0 === r1 && r1 === r2) return { category: 'THREE_OF_A_KIND', value: pack(CATEGORY_RANK.THREE_OF_A_KIND, r0) };
  // A-2-3 is the lowest straight (3 high); A-K-Q the highest.
  let high: number | null = null;
  if (r0 - 1 === r1 && r1 - 1 === r2) high = r0;
  else if (r0 === 14 && r1 === 3 && r2 === 2) high = 3;
  if (high !== null) {
    const category = flush ? 'STRAIGHT_FLUSH' : 'STRAIGHT';
    return { category, value: pack(CATEGORY_RANK[category], high) };
  }
  if (flush) return { category: 'FLUSH', value: pack(CATEGORY_RANK.FLUSH, r0, r1, r2) };
  if (r0 === r1 || r1 === r2) return { category: 'PAIR', value: pack(CATEGORY_RANK.PAIR, r1, r0 === r1 ? r2 : r0) };
  return { category: 'HIGH_CARD', value: pack(CATEGORY_RANK.HIGH_CARD, r0, r1, r2) };
}

/** >0 if hand a beats hand b, <0 if b beats a, 0 when they tie. */
export const threeCardPokerCompare = (a: readonly CardId[], b: readonly CardId[]): number => threeCardPokerEvaluate(a).value - threeCardPokerEvaluate(b).value;

/** The dealer needs queen-high or better to play. */
export function threeCardPokerDealerQualifies(cards: readonly CardId[]): boolean {
  if (threeCardPokerEvaluate(cards).category !== 'HIGH_CARD') return true;
  return Math.max(...cards.map((c) => parseCard(c).rank)) >= 12;
}

export interface ThreeCardPokerConfig {
  minBet: number;
  maxBet: number;
  minPlayers: number;
  maxPlayers: number;
  startDelayMs: number;
  dealDelayMs: number;
  handEndDelayMs: number;
}

export type ThreeCardPokerPhase = 'WAITING' | 'BETTING' | 'DEALING' | 'DECIDING' | 'REVEAL' | 'HAND_END';
export type ThreeCardPokerSeatStatus = 'WAITING' | 'BET' | 'SIT_OUT';
export type ThreeCardPokerDecision = 'PLAY' | 'FOLD';
export type ThreeCardPokerOutcome = 'WIN' | 'LOSE' | 'PUSH' | 'NO_QUALIFY' | 'FOLD';

export interface ThreeCardPokerSeat {
  playerId: string;
  chips: number;
  status: ThreeCardPokerSeatStatus;
  ante: number;
  pairPlus: number;
  /** The play bet (equal to the ante) once the player has chosen to play. */
  play: number;
  decision: ThreeCardPokerDecision | null;
  cards: CardId[];
  /** What came back at the end of the round (stakes plus winnings, or 0). */
  payout: number;
}

export interface ThreeCardPokerResultSeat {
  seat: number;
  ante: number;
  play: number;
  pairPlus: number;
  outcome: ThreeCardPokerOutcome;
  /** Null when the player folded: a folded hand is never shown. */
  cards: CardId[] | null;
  category: ThreeCardPokerCategory;
  anteBonus: number;
  pairPlusWin: number;
  payout: number;
  delta: number;
}

export interface ThreeCardPokerResult {
  handNo: number;
  dealer: CardId[];
  dealerCategory: ThreeCardPokerCategory;
  qualified: boolean;
  seats: ThreeCardPokerResultSeat[];
}

export interface ThreeCardPokerState {
  config: ThreeCardPokerConfig;
  seats: (ThreeCardPokerSeat | null)[];
  phase: ThreeCardPokerPhase;
  handNo: number;
  /** Seat to bet or to decide. */
  turn: number;
  deck: CardId[];
  dealer: CardId[];
  lastResult: ThreeCardPokerResult | null;
  handsPlayed: number;
}

export type ThreeCardPokerAction = { type: 'bet'; ante: number; pairPlus: number } | { type: 'play' } | { type: 'fold' } | { type: 'sitOut' };

export type ThreeCardPokerLegal =
  | { kind: 'bet'; min: number; max: number; pairPlusMax: number; canSitOut: boolean }
  | { kind: 'decide'; play: number }
  | { kind: 'none' };

export interface ThreeCardPokerViewSeat {
  seat: number;
  playerId: string;
  chips: number;
  status: ThreeCardPokerSeatStatus;
  ante: number;
  pairPlus: number;
  play: number;
  decision: ThreeCardPokerDecision | null;
  hasCards: boolean;
  payout: number;
}

export interface ThreeCardPokerView {
  gameType: 'THREE_CARD_POKER';
  config: ThreeCardPokerConfig;
  seat: number | null;
  phase: ThreeCardPokerPhase;
  handNo: number;
  turn: number;
  /** My own three cards (empty before the deal or when I am not in the round). */
  hand: CardId[];
  myCategory: ThreeCardPokerCategory | null;
  /** The dealer's cards, shown only once every player has decided. */
  dealer: CardId[];
  deckCount: number;
  players: (ThreeCardPokerViewSeat | null)[];
  lastResult: ThreeCardPokerResult | null;
}

const DEFAULTS: ThreeCardPokerConfig = {
  minBet: 10,
  maxBet: 1000,
  minPlayers: 2,
  maxPlayers: 6,
  startDelayMs: 2000,
  dealDelayMs: 1500,
  handEndDelayMs: 5000,
};

function seatsWhere(s: ThreeCardPokerState, pred: (p: ThreeCardPokerSeat, i: number) => boolean): number[] {
  const out: number[] = [];
  s.seats.forEach((p, i) => {
    if (p && pred(p, i)) out.push(i);
  });
  return out;
}

/** The ante and the play bet both come out of the stack, so a player needs two minimum bets to sit down. */
const canJoin = (s: ThreeCardPokerState, p: ThreeCardPokerSeat): boolean => p.chips >= s.config.minBet * 2;
const canStart = (s: ThreeCardPokerState): boolean => seatsWhere(s, (p) => canJoin(s, p)).length >= s.config.minPlayers;

function advanceBetting(s: ThreeCardPokerState, from: number): void {
  for (let seat = from + 1; seat < s.seats.length; seat++) {
    const p = s.seats[seat];
    if (p && p.status === 'WAITING') {
      s.turn = seat;
      return;
    }
  }
  s.turn = -1;
  s.phase = seatsWhere(s, (p) => p.status === 'BET').length > 0 ? 'DEALING' : 'WAITING';
}

function advanceDeciding(s: ThreeCardPokerState, from: number): void {
  for (let seat = from + 1; seat < s.seats.length; seat++) {
    const p = s.seats[seat];
    if (p && p.status === 'BET' && p.decision === null) {
      s.turn = seat;
      return;
    }
  }
  s.turn = -1;
  s.phase = 'REVEAL';
}

function startHand(s: ThreeCardPokerState, rng: Rng, events: EngineEvent[]): void {
  s.handNo++;
  s.deck = shuffle(buildDeck(), rng);
  s.dealer = [];
  for (const p of s.seats) {
    if (!p) continue;
    p.ante = 0;
    p.pairPlus = 0;
    p.play = 0;
    p.decision = null;
    p.cards = [];
    p.payout = 0;
    p.status = canJoin(s, p) ? 'WAITING' : 'SIT_OUT';
  }
  s.phase = 'BETTING';
  s.turn = -1;
  events.push({ type: 'handStart', handNo: s.handNo });
  advanceBetting(s, -1);
  if (s.phase !== 'BETTING') events.push({ type: 'waitingForPlayers' });
}

function settle(s: ThreeCardPokerState, events: EngineEvent[]): void {
  const dealerHand = threeCardPokerEvaluate(s.dealer);
  const qualified = threeCardPokerDealerQualifies(s.dealer);
  const seats: ThreeCardPokerResultSeat[] = [];
  s.seats.forEach((p, seat) => {
    if (!p || p.status !== 'BET') return;
    const mine = threeCardPokerEvaluate(p.cards);
    const folded = p.decision !== 'PLAY';
    let payout = 0;
    let outcome: ThreeCardPokerOutcome = 'FOLD';
    let anteBonus = 0;
    if (!folded) {
      anteBonus = p.ante * (THREE_CARD_POKER_ANTE_BONUS[mine.category] ?? 0);
      const cmp = mine.value - dealerHand.value;
      if (!qualified) {
        outcome = 'NO_QUALIFY';
        payout = p.ante * 2 + p.play;
      } else if (cmp > 0) {
        outcome = 'WIN';
        payout = (p.ante + p.play) * 2;
      } else if (cmp < 0) {
        outcome = 'LOSE';
      } else {
        outcome = 'PUSH';
        payout = p.ante + p.play;
      }
      payout += anteBonus;
    }
    const mult = THREE_CARD_POKER_PAIR_PLUS[mine.category] ?? 0;
    const pairPlusWin = p.pairPlus > 0 ? p.pairPlus * mult : 0;
    if (p.pairPlus > 0 && mult > 0) payout += p.pairPlus + pairPlusWin;
    p.payout = payout;
    p.chips += payout;
    const staked = p.ante + p.play + p.pairPlus;
    seats.push({ seat, ante: p.ante, play: p.play, pairPlus: p.pairPlus, outcome, cards: folded ? null : [...p.cards], category: mine.category, anteBonus, pairPlusWin, payout, delta: payout - staked });
  });
  s.lastResult = { handNo: s.handNo, dealer: [...s.dealer], dealerCategory: dealerHand.category, qualified, seats };
  s.handsPlayed++;
  s.phase = 'HAND_END';
  events.push({ type: 'handEnd', handNo: s.handNo, qualified, results: seats.map((r) => ({ seat: r.seat, delta: r.delta })) });
}

export const threeCardPokerEngine: GameEngine<ThreeCardPokerConfig, ThreeCardPokerState, ThreeCardPokerAction, ThreeCardPokerView, ThreeCardPokerLegal> = {
  gameType: 'THREE_CARD_POKER',
  kind: 'session',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('maxPlayers', c.maxPlayers, 1, 8);
    checkInt('minPlayers', c.minPlayers, 1, c.maxPlayers);
    checkInt('minBet', c.minBet, 1, 1e9);
    checkInt('maxBet', c.maxBet, c.minBet, 1e12);
    checkInt('startDelayMs', c.startDelayMs, 0, 60000);
    checkInt('dealDelayMs', c.dealDelayMs, 0, 60000);
    checkInt('handEndDelayMs', c.handEndDelayMs, 0, 60000);
    return c;
  },

  seatLimits: (c) => ({ min: c.minPlayers, max: c.maxPlayers }),

  createState(config, seats, rng) {
    const s: ThreeCardPokerState = {
      config,
      seats: Array<ThreeCardPokerSeat | null>(config.maxPlayers).fill(null),
      phase: 'WAITING',
      handNo: 0,
      turn: -1,
      deck: shuffle(buildDeck(), rng),
      dealer: [],
      lastResult: null,
      handsPlayed: 0,
    };
    for (const setup of seats) this.addSeat!(s, setup);
    return s;
  },

  addSeat(s, setup: SeatSetup) {
    assertEngine(setup.seat >= 0 && setup.seat < s.seats.length && !s.seats[setup.seat], 'SEAT_TAKEN', 'Seat unavailable');
    const chips = setup.chips ?? 0;
    assertEngine(Number.isInteger(chips) && chips >= 0, 'INVALID_AMOUNT', 'Invalid chips');
    // While bets are still being taken a later seat can join in; once the cards are coming they wait.
    const open = s.phase === 'WAITING' || s.phase === 'HAND_END' || (s.phase === 'BETTING' && chips >= s.config.minBet * 2);
    s.seats[setup.seat] = { playerId: setup.playerId, chips, status: open ? 'WAITING' : 'SIT_OUT', ante: 0, pairPlus: 0, play: 0, decision: null, cards: [], payout: 0 };
    return [{ type: 'playerJoined', seat: setup.seat, playerId: setup.playerId, chips }];
  },

  removeSeat(s, seat) {
    const p = s.seats[seat];
    assertEngine(p, 'INVALID_ACTION', 'Seat empty');
    let chips = p.chips;
    const wasTurn = s.turn === seat;
    if (s.phase === 'BETTING') {
      // Nothing is dealt yet: the bets come back.
      chips += p.ante + p.pairPlus;
      s.seats[seat] = null;
      if (wasTurn) advanceBetting(s, seat);
    } else {
      // Leaving once the cards are out forfeits the bets on the table.
      s.seats[seat] = null;
      if (wasTurn && s.phase === 'DECIDING') advanceDeciding(s, seat);
    }
    return { chips, events: [{ type: 'playerLeft', seat }] };
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'sitOut' || raw.type === 'play' || raw.type === 'fold') return { type: raw.type };
    if (raw.type === 'bet' && typeof raw.ante === 'number' && Number.isInteger(raw.ante)) {
      const pairPlus = raw.pairPlus === undefined ? 0 : raw.pairPlus;
      if (typeof pairPlus === 'number' && Number.isInteger(pairPlus)) return { type: 'bet', ante: raw.ante, pairPlus };
    }
    throw new EngineError('INVALID_ACTION', 'Unknown Three Card Poker action');
  },

  applyAction(s, seat, action) {
    const p = s.seats[seat];
    assertEngine(p, 'INVALID_ACTION', 'You are not at this table');
    assertEngine(s.phase === 'BETTING' || s.phase === 'DECIDING', 'INVALID_PHASE', 'Not your time to act');
    assertEngine(seat === s.turn, 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];
    if (s.phase === 'BETTING') {
      assertEngine(action.type === 'bet' || action.type === 'sitOut', 'INVALID_PHASE', 'Place your bet first');
      if (action.type === 'sitOut') {
        p.status = 'SIT_OUT';
        events.push({ type: 'sitOut', seat });
        advanceBetting(s, seat);
        return events;
      }
      const { ante, pairPlus } = action;
      const maxAnte = Math.min(s.config.maxBet, Math.floor(p.chips / 2));
      assertEngine(ante >= s.config.minBet && ante <= maxAnte, 'INVALID_AMOUNT', `Ante between ${s.config.minBet} and ${maxAnte}`);
      assertEngine(pairPlus === 0 || (pairPlus >= s.config.minBet && pairPlus <= s.config.maxBet), 'INVALID_AMOUNT', `Pair Plus is 0 or between ${s.config.minBet} and ${s.config.maxBet}`);
      assertEngine(ante * 2 + pairPlus <= p.chips, 'INVALID_AMOUNT', 'Not enough chips to cover the ante, the play bet and Pair Plus');
      p.chips -= ante + pairPlus;
      p.ante = ante;
      p.pairPlus = pairPlus;
      p.status = 'BET';
      events.push({ type: 'bet', seat, ante, pairPlus });
      advanceBetting(s, seat);
      return events;
    }
    assertEngine(action.type === 'play' || action.type === 'fold', 'INVALID_PHASE', 'Play or fold');
    if (action.type === 'play') {
      // The chips for the play bet were set aside when the ante was placed.
      p.chips -= p.ante;
      p.play = p.ante;
      p.decision = 'PLAY';
      events.push({ type: 'play', seat, amount: p.play });
    } else {
      p.decision = 'FOLD';
      events.push({ type: 'fold', seat });
    }
    advanceDeciding(s, seat);
    return events;
  },

  getActors(s) {
    return (s.phase === 'BETTING' || s.phase === 'DECIDING') && s.turn >= 0 && s.seats[s.turn] ? [s.turn] : [];
  },

  getLegalActions(s, seat) {
    const p = s.seats[seat];
    if (!p || seat !== s.turn) return { kind: 'none' };
    if (s.phase === 'BETTING') {
      const max = Math.min(s.config.maxBet, Math.floor(p.chips / 2));
      return { kind: 'bet', min: s.config.minBet, max, pairPlusMax: Math.max(0, Math.min(s.config.maxBet, p.chips - 2 * s.config.minBet)), canSitOut: true };
    }
    if (s.phase === 'DECIDING') return { kind: 'decide', play: p.ante };
    return { kind: 'none' };
  },

  getTimeoutAction(s, seat) {
    // A player who cannot be reached after the deal forfeits the ante; before it they sit the round out.
    return s.phase === 'DECIDING' && s.turn === seat ? { type: 'fold' } : { type: 'sitOut' };
  },

  getView(s, seat) {
    const me = seat !== null ? s.seats[seat] : null;
    const hand = me && me.status === 'BET' ? [...me.cards] : [];
    return {
      gameType: 'THREE_CARD_POKER',
      config: s.config,
      seat,
      phase: s.phase,
      handNo: s.handNo,
      turn: s.turn,
      hand,
      myCategory: hand.length === 3 ? threeCardPokerEvaluate(hand).category : null,
      dealer: s.phase === 'HAND_END' ? [...s.dealer] : [],
      deckCount: s.deck.length,
      players: s.seats.map((p, i) =>
        p ? { seat: i, playerId: p.playerId, chips: p.chips, status: p.status, ante: p.ante, pairPlus: p.pairPlus, play: p.play, decision: p.decision, hasCards: p.cards.length > 0, payout: p.payout } : null,
      ),
      lastResult: s.lastResult ? structuredClone(s.lastResult) : null,
    };
  },

  getSystemStep(s) {
    if (s.phase === 'DEALING') return { kind: 'deal', delayMs: s.config.dealDelayMs };
    if (s.phase === 'REVEAL') return { kind: 'reveal', delayMs: s.config.dealDelayMs };
    if (s.phase === 'HAND_END') return { kind: 'nextHand', delayMs: s.config.handEndDelayMs };
    if (s.phase === 'WAITING' && canStart(s)) return { kind: 'startHand', delayMs: s.config.startDelayMs };
    return null;
  },

  runSystemStep(s, rng) {
    const events: EngineEvent[] = [];
    if (s.phase === 'DEALING') {
      const players = seatsWhere(s, (p) => p.status === 'BET');
      if (s.deck.length < (players.length + 1) * 3) s.deck = shuffle(buildDeck(), rng);
      for (const seat of players) s.seats[seat]!.cards = [s.deck.pop()!, s.deck.pop()!, s.deck.pop()!];
      s.dealer = [s.deck.pop()!, s.deck.pop()!, s.deck.pop()!];
      s.phase = 'DECIDING';
      events.push({ type: 'cardsDealt', seats: players });
      advanceDeciding(s, -1);
      return events;
    }
    if (s.phase === 'REVEAL') {
      settle(s, events);
      return events;
    }
    if (s.phase === 'HAND_END' || s.phase === 'WAITING') {
      s.phase = 'WAITING';
      if (canStart(s)) startHand(s, rng, events);
      else events.push({ type: 'waitingForPlayers' });
    }
    return events;
  },

  isFinished: () => false,
  isHandInProgress: (s) => s.phase === 'DEALING' || s.phase === 'DECIDING' || s.phase === 'REVEAL',
  getChips: (s) => Object.fromEntries(seatsWhere(s, () => true).map((i) => [i, s.seats[i]!.chips])),
  getStandings: (s) => standingsFromScores(seatsWhere(s, () => true).map((seat) => ({ seat, score: s.seats[seat]!.chips }))),
  getRoundsCompleted: (s) => s.handsPlayed,
};
